import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import { Document, Page, pdfjs } from 'react-pdf';
import PropTypes from 'prop-types';
import { compact, map } from 'lodash';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import LoadingModule from 'common/components/LoadingModule';
import { getTransferOutDocumentApi } from 'api/patient';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { casingFormat } from 'common/utils';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

const ItemLabelValue = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Grid container sx={{ mb: 1 }}>
      <Grid item xs={4}>
        <Typography
          variant='body2'
          sx={{ color: lighten(theme.palette.text.primary, 0.3), fontWeight: 600 }}
        >
          {label}:
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant='body2'>{value || 'N/A'}</Typography>
      </Grid>
    </Grid>
  );
};

ItemLabelValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

ItemLabelValue.defaultProps = {
  value: undefined,
};

const TransferOutDocumentViewer = ({ lineItemId, open, onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [numPages, setNumPages] = useState(null);
  const [transferDocument, setTransferDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /* istanbul ignore next */
  const mobilePdfWidth = useMediaQuery(theme.breakpoints.down('sm')) && 300;
  /* istanbul ignore next */
  const tabletPdfWidth = useMediaQuery(theme.breakpoints.down('md')) && 600;
  /* istanbul ignore next */
  const desktopPdfWidth = useMediaQuery(theme.breakpoints.up('md')) && 900;

  const pdfWidth = mobilePdfWidth || tabletPdfWidth || desktopPdfWidth;

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => setNumPages(nextNumPages);

  useEffect(() => {
    if (open && lineItemId && !transferDocument && !isLoading) {
      setIsLoading(true);
      getTransferOutDocumentApi({ lineItemId })
        .then((data) => {
          setTransferDocument(data);
          setIsLoading(false);
        })
        .catch((error) => {
          dispatch(handleError({ error }));
          setIsLoading(false);
        });
    }
    // Reset when modal closes
    if (!open) {
      setTransferDocument(null);
      setNumPages(null);
    }
  }, [isLoading, transferDocument, lineItemId, open, dispatch]);

  const handleClose = () => {
    setTransferDocument(null);
    setNumPages(null);
    onClose();
  };

  const formatAddress = (pharmacy) => {
    return compact([
      pharmacy.addressLine1,
      pharmacy.addressLine2,
      pharmacy.city,
      pharmacy.state,
      pharmacy.zip,
    ]).join(', ');
  };

  const formatTransferMethod = (method) => {
    if (!method) return 'N/A';
    return method
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <Dialog
      open={open}
      maxWidth='lg'
      fullWidth
      fullScreen={!!mobilePdfWidth}
      aria-labelledby='TransferOutDocumentViewer-header'
      onClose={handleClose}
    >
      <DialogTitle id='TransferOutDocumentViewer-header' sx={{ pb: 1 }}>
        Transfer Out Document
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {isLoading && <LoadingModule height='25vh' />}

        {!isLoading && transferDocument && (
          <Grid container>
            {/* Transfer Details Section */}
            <Grid item xs={12} sx={{ px: 3, pt: 2, pb: 1 }}>
              <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                Transfer Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ItemLabelValue
                    label='Transfer Method'
                    value={formatTransferMethod(transferDocument.transferMethod)}
                  />
                  <ItemLabelValue
                    label='Transfer Status'
                    value={transferDocument.transferStatus?.replace(/_/g, ' ')}
                  />
                  <ItemLabelValue
                    label='Transfer Date'
                    value={
                      transferDocument.createdAt
                        ? format(new Date(transferDocument.createdAt), 'MM/dd/yyyy hh:mm aaa')
                        : 'N/A'
                    }
                  />
                  <ItemLabelValue label='File Name' value={transferDocument.fileName} />
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={{ width: '100%' }} />

            {/* Pharmacy Details Section */}
            {transferDocument.transferPharmacy && (
              <>
                <Grid item xs={12} sx={{ px: 3, pt: 2, pb: 1 }}>
                  <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                    Transfer Pharmacy Details
                  </Typography>
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <ItemLabelValue
                        label='Pharmacy Name'
                        value={casingFormat(transferDocument.transferPharmacy.pharmacyName)}
                      />
                      <ItemLabelValue
                        label='Address'
                        value={formatAddress(transferDocument.transferPharmacy)}
                      />
                      <ItemLabelValue
                        label='Phone'
                        value={transferDocument.transferPharmacy.phone}
                      />
                      <ItemLabelValue label='Fax' value={transferDocument.transferPharmacy.fax} />
                      <ItemLabelValue label='NPI' value={transferDocument.transferPharmacy.npi} />
                      <ItemLabelValue label='DEA' value={transferDocument.transferPharmacy.dea} />
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ width: '100%' }} />
              </>
            )}

            {/* PDF Viewer Section */}
            <Grid item xs={12} sx={{ px: 3, pt: 2, pb: 2 }}>
              <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                Transfer Document
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  maxHeight: '70vh',
                  overflow: 'auto',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 2,
                }}
              >
                {transferDocument.presignedUrl && (
                  <Document
                    file={transferDocument.presignedUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    error={<Box sx={{ p: 3 }}>Failed to load transfer document</Box>}
                    loading={<LoadingModule height='25vh' />}
                  >
                    {map(new Array(numPages), (_, index) => (
                      <Page pageNumber={index + 1} width={pdfWidth} key={index} />
                    ))}
                  </Document>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          disabled={!transferDocument?.presignedUrl}
          component={Link}
          href={transferDocument?.presignedUrl}
          target='_blank'
          download={transferDocument?.fileName}
          variant='outlined'
          color='secondary'
          sx={{ mr: 2, minWidth: 150 }}
        >
          Download PDF
        </Button>

        <Button onClick={handleClose} variant='contained' sx={{ minWidth: 150 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TransferOutDocumentViewer.propTypes = {
  lineItemId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TransferOutDocumentViewer;
